import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { eventsService } from '../../services/api';
import { FaEdit, FaTrash, FaPlus, FaSearch, FaEye, FaUsers } from 'react-icons/fa';
import Loading from '../../components/Common/Loading';

const EventsList = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [eventToDelete, setEventToDelete] = useState(null);
  const [showRegistrations, setShowRegistrations] = useState(null);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const response = await eventsService.getAll();
      
      console.log('Events API Response:', response);
      
      // CORRECTION : Gérer les différents formats de réponse
      let eventsData = [];
      
      if (response.data) {
        // Format 1: { success: true, data: [...] }
        if (response.data.success && Array.isArray(response.data.data)) {
          eventsData = response.data.data;
        }
        // Format 2: { data: [...] }
        else if (Array.isArray(response.data.data)) {
          eventsData = response.data.data;
        }
        // Format 3: Directement un tableau
        else if (Array.isArray(response.data)) {
          eventsData = response.data;
        }
        else {
          console.warn('Format de réponse non reconnu pour events:', response.data);
          eventsData = [];
        }
      }
      
      console.log('Events data extraits:', eventsData);
      setEvents(eventsData);
      
    } catch (error) {
      console.error('Error fetching events:', error);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!eventToDelete) return;
    try {
      await eventsService.delete(eventToDelete.id);
      // Re-fetch après suppression
      await fetchEvents();
      setShowDeleteModal(false);
      setEventToDelete(null);
    } catch (error) {
      console.error('Error deleting event:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const handleViewRegistrations = async (event) => {
    try {
      const response = await eventsService.getRegistrations(event.id);
      console.log('Registrations response:', response);
      
      let registrationsData = [];
      if (response.data) {
        if (Array.isArray(response.data)) {
          registrationsData = response.data;
        } else if (response.data.success && Array.isArray(response.data.data)) {
          registrationsData = response.data.data;
        } else if (Array.isArray(response.data.data)) {
          registrationsData = response.data.data;
        }
      }
      
      setShowRegistrations({ event, registrations: registrationsData });
    } catch (error) {
      console.error('Error fetching registrations:', error);
      alert('Erreur lors du chargement des inscriptions');
    }
  };

  // CORRECTION : Vérifier que events est un tableau avant filter
  const filteredEvents = Array.isArray(events) ? events.filter(event => {
    if (!event) return false;
    const searchLower = searchTerm.toLowerCase();
    return (
      (event.title?.toLowerCase().includes(searchLower) || false) ||
      (event.location?.toLowerCase().includes(searchLower) || false)
    );
  }) : [];

  if (loading) return <Loading />;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Gestion des événements</h1>
          <p className="mt-1 text-gray-600">{filteredEvents.length} événement(s)</p>
        </div>
        <Link to="/events/new" className="inline-flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700">
          <FaPlus />
          Nouvel événement
        </Link>
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="relative">
          <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher un événement..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:border-purple-500"
          />
        </div>
      </div>

      {/* Events Grid */}
      {filteredEvents.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md p-12 text-center text-gray-500">
          <p>Aucun événement trouvé</p>
          <Link to="/events/new" className="mt-4 inline-block text-purple-600 hover:text-purple-800">
            + Créer votre premier événement
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredEvents.map((event) => (
            <div key={event.id} className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition">
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="font-semibold text-lg">{event.title}</h3>
                  <div className="flex space-x-2">
                    <Link to={`/events/${event.id}`} className="text-blue-600 hover:text-blue-800" title="Voir">
                      <FaEye />
                    </Link>
                    <Link to={`/events/edit/${event.id}`} className="text-green-600 hover:text-green-800" title="Modifier">
                      <FaEdit />
                    </Link>
                    <button
                      onClick={() => {
                        setEventToDelete(event);
                        setShowDeleteModal(true);
                      }}
                      className="text-red-600 hover:text-red-800"
                      title="Supprimer"
                    >
                      <FaTrash />
                    </button>
                  </div>
                </div>
                
                <div className="space-y-2 text-sm text-gray-600">
                  <div>📅 {new Date(event.event_date).toLocaleDateString('fr-FR')} à {event.event_time}</div>
                  <div>📍 {event.location}</div>
                  <div>👥 {event.registered_count || 0} / {event.capacity || '∞'} inscrits</div>
                </div>
                
                {event.description && (
                  <p className="mt-3 text-gray-600 text-sm line-clamp-2">{event.description}</p>
                )}
                
                <button
                  onClick={() => handleViewRegistrations(event)}
                  className="mt-4 w-full py-2 border border-purple-600 text-purple-600 rounded-lg hover:bg-purple-50 transition flex items-center justify-center gap-2"
                >
                  <FaUsers />
                  Voir les inscriptions
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && eventToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-4">Confirmer la suppression</h3>
            <p className="text-gray-600 mb-6">Supprimer "{eventToDelete.title}" ?</p>
            <div className="flex justify-end gap-4">
              <button onClick={() => setShowDeleteModal(false)} className="px-4 py-2 border rounded-lg">Annuler</button>
              <button onClick={handleDelete} className="px-4 py-2 bg-red-600 text-white rounded-lg">Supprimer</button>
            </div>
          </div>
        </div>
      )}

      {/* Registrations Modal */}
      {showRegistrations && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-semibold">Inscriptions - {showRegistrations.event.title}</h3>
                <button onClick={() => setShowRegistrations(null)} className="text-gray-500">✕</button>
              </div>
              
              {!showRegistrations.registrations || showRegistrations.registrations.length === 0 ? (
                <p className="text-center text-gray-500 py-8">Aucune inscription</p>
              ) : (
                <div className="space-y-3">
                  {showRegistrations.registrations.map((reg, idx) => (
                    <div key={idx} className="p-4 bg-gray-50 rounded-lg">
                      <p className="font-medium">{reg.name}</p>
                      <p className="text-sm text-gray-600">{reg.email}</p>
                      {reg.phone && <p className="text-sm text-gray-600">📞 {reg.phone}</p>}
                      <p className="text-xs text-gray-400 mt-1">Inscrit le {new Date(reg.registered_at).toLocaleDateString('fr-FR')}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventsList;